import { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "sonner";
import { Loader2, TrendingUp, Send, Users, DollarSign, Zap, Calendar } from "lucide-react";
import { Badge } from "@/components/ui/badge";

const SEGMENT_LABELS = {
  vip: "VIP",
  regular: "Regular",
  new: "Novo",
  at_risk: "Em Risco",
  lost: "Perdido",
};

const TYPE_LABELS = {
  reactivation: "Reativação",
  vip_reward: "Recompensa VIP",
  welcome: "Boas-vindas",
};

function RoiBadge({ roi }) {
  if (!roi || roi === 0) return <span className="text-gray-400 text-sm">—</span>;
  const color = roi >= 3 ? "bg-emerald-100 text-emerald-700" : roi >= 1.5 ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700";
  return <Badge className={`${color} text-xs font-bold`}>{roi.toFixed(1)}x</Badge>;
}

function StatCard({ icon: Icon, label, value, color = "text-gray-800" }) {
  return (
    <div className="bg-white rounded-xl p-4 border shadow-sm">
      <div className="flex items-center gap-2 mb-1">
        <Icon className="w-4 h-4 text-gray-400" />
        <p className="text-xs text-gray-400 uppercase tracking-wide">{label}</p>
      </div>
      <p className={`text-2xl font-bold ${color}`}>{value}</p>
    </div>
  );
}

export function CampaignROITab({ authAxios }) {
  const axiosRef = useRef(authAxios);
  axiosRef.current = authAxios;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(90);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosRef.current.get(`/intelligence/crm/campaigns/roi?days=${days}`);
      setData(res.data);
    } catch {
      toast.error("Erro ao carregar dados de campanhas");
    }
    setLoading(false);
  }, [days]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-emerald-600" />
      </div>
    );
  }

  const campaigns = data?.campaigns || [];
  const totalSent = campaigns.reduce((s, c) => s + c.sent, 0);
  const totalConverted = campaigns.reduce((s, c) => s + c.converted, 0);
  const totalRevenue = campaigns.reduce((s, c) => s + c.revenue, 0);
  const avgConvRate = totalSent > 0 ? (totalConverted / totalSent * 100).toFixed(1) : "0.0";

  return (
    <div className="space-y-5">
      {/* Period selector */}
      <div className="flex items-center justify-between">
        <h2 className="font-bold text-gray-800 text-sm">ROI por Campanha</h2>
        <select
          value={days}
          onChange={e => setDays(Number(e.target.value))}
          className="text-xs border rounded-lg px-2 py-1 bg-white"
        >
          <option value={30}>Últimos 30 dias</option>
          <option value={90}>Últimos 90 dias</option>
          <option value={180}>Últimos 6 meses</option>
          <option value={365}>Último ano</option>
        </select>
      </div>

      {/* Summary KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard icon={Send} label="Campanhas" value={campaigns.length} />
        <StatCard icon={Users} label="Enviadas" value={totalSent.toLocaleString()} />
        <StatCard icon={TrendingUp} label="Taxa Conv." value={`${avgConvRate}%`} color="text-blue-600" />
        <StatCard icon={DollarSign} label="Receita" value={`€${totalRevenue.toFixed(0)}`} color="text-emerald-600" />
      </div>

      {/* Campaign list */}
      {campaigns.length === 0 ? (
        <div className="bg-white rounded-xl border p-12 text-center">
          <Zap className="w-10 h-10 text-gray-200 mx-auto mb-3" />
          <p className="text-gray-400 text-sm">Nenhuma campanha nos últimos {days} dias</p>
          <p className="text-gray-300 text-xs mt-1">Lance campanhas no separador CRM</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left p-3 text-xs font-semibold text-gray-500 uppercase">Campanha</th>
                <th className="text-right p-3 text-xs font-semibold text-gray-500 uppercase">Enviadas</th>
                <th className="text-right p-3 text-xs font-semibold text-gray-500 uppercase">Usaram</th>
                <th className="text-right p-3 text-xs font-semibold text-gray-500 uppercase">Conv.</th>
                <th className="text-right p-3 text-xs font-semibold text-gray-500 uppercase">Receita</th>
                <th className="text-right p-3 text-xs font-semibold text-gray-500 uppercase">ROI</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {campaigns.map(camp => (
                <tr key={camp.id} className="hover:bg-gray-50 transition-colors">
                  <td className="p-3">
                    <div className="flex items-center gap-2">
                      <div>
                        <p className="font-medium text-gray-800">
                          {TYPE_LABELS[camp.type] || camp.type}
                          {camp.discount_pct > 0 && (
                            <span className="ml-1 text-xs text-gray-400">(-{camp.discount_pct}%)</span>
                          )}
                        </p>
                        <div className="flex items-center gap-1 mt-0.5">
                          <Calendar className="w-3 h-3 text-gray-300" />
                          <span className="text-xs text-gray-400">
                            {camp.created_at ? new Date(camp.created_at).toLocaleDateString("pt-BR", { day: "2-digit", month: "short" }) : ""}
                          </span>
                          {camp.target_segment && (
                            <Badge variant="outline" className="text-[10px] px-1 py-0 ml-1">
                              {SEGMENT_LABELS[camp.target_segment] || camp.target_segment}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="p-3 text-right font-medium text-gray-700">{camp.sent}</td>
                  <td className="p-3 text-right font-medium text-gray-700">{camp.converted}</td>
                  <td className="p-3 text-right">
                    <span className={`text-sm font-medium ${camp.conversion_rate >= 10 ? "text-emerald-600" : camp.conversion_rate >= 5 ? "text-amber-600" : "text-gray-500"}`}>
                      {camp.conversion_rate}%
                    </span>
                  </td>
                  <td className="p-3 text-right font-bold text-emerald-600">
                    {camp.revenue > 0 ? `€${camp.revenue.toFixed(2)}` : <span className="text-gray-300 font-normal">€0</span>}
                  </td>
                  <td className="p-3 text-right">
                    <RoiBadge roi={camp.roi} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-xs text-gray-400 text-center">
        ROI = receita gerada ÷ desconto total concedido. Actualiza automaticamente quando cupões são usados em pedidos.
      </p>
    </div>
  );
}
